import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { X, Play, Pencil, Trash2, Plus } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    type: 'image',
    url: '',
    title: '',
    description: '',
    thumbnail: ''
  });

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryItems(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load gallery items',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId, event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await axios.delete(`${API}/gallery/${itemId}`);
      toast({
        title: 'Success',
        description: 'Gallery item deleted successfully',
      });
      fetchGalleryItems();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete item',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (item, event) => {
    event.stopPropagation();
    setEditingItem({...item});
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/gallery/${editingItem.id}`, {
        url: editingItem.url,
        title: editingItem.title,
        description: editingItem.description,
        thumbnail: editingItem.thumbnail
      });
      toast({
        title: 'Success',
        description: 'Gallery item updated successfully',
      });
      setEditMode(false);
      setEditingItem(null);
      fetchGalleryItems();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update item',
        variant: 'destructive',
      });
    }
  };

  const handleAddItem = async () => {
    if (!newItem.url || !newItem.title) {
      toast({
        title: 'Error',
        description: 'URL and title are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      await axios.post(`${API}/gallery`, newItem);
      toast({
        title: 'Success',
        description: 'Gallery item added successfully',
      });
      setShowAddForm(false);
      setNewItem({
        type: 'image',
        url: '',
        title: '',
        description: '',
        thumbnail: ''
      });
      fetchGalleryItems();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add item',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  const images = galleryItems.filter(item => item.type === 'image');
  const videos = galleryItems.filter(item => item.type === 'video');

  return (
    <div className="min-h-screen bg-slate-50 py-12 viking-pattern-section">
      <div className="viking-pattern-bg"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Gallery
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Explore the power and beauty of Viking funeral traditions through stunning imagery and historical reenactments
          </p>
          {isAdmin && (
            <div className="mt-6">
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Gallery Item
              </Button>
            </div>
          )}
        </div>

        {/* Images Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Images</h2>
          {images.length === 0 ? (
            <p className="text-slate-600 text-center py-8">No images yet</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images.map((image) => (
                <Card
                  key={image.id}
                  className="overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 relative"
                  onClick={() => setSelectedMedia({ type: 'image', ...image })}
                >
                  {isAdmin && (
                    <div className="absolute top-2 right-2 z-10 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="bg-white/90 hover:bg-white"
                        onClick={(e) => handleEdit(image, e)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => handleDelete(image.id, e)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="font-semibold text-lg mb-1">{image.title}</h3>
                        <p className="text-sm text-slate-200">{image.description}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Videos Section */}
        <section>
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Videos</h2>
          {videos.length === 0 ? (
            <p className="text-slate-600 text-center py-8">No videos yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <Card
                  key={video.id}
                  className="overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 relative"
                  onClick={() => setSelectedMedia({ type: 'video', ...video })}
                >
                  {isAdmin && (
                    <div className="absolute top-2 right-2 z-10 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="bg-white/90 hover:bg-white"
                        onClick={(e) => handleEdit(video, e)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => handleDelete(video.id, e)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-colors duration-300">
                      <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-lg mb-2">{video.title}</h3>
                    <p className="text-sm text-slate-600">{video.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* View Media Modal */}
      {selectedMedia && (
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="max-w-6xl w-full p-0 bg-black border-0">
            <button
              onClick={() => setSelectedMedia(null)}
              className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {selectedMedia.type === 'image' ? (
              <div className="relative">
                <img
                  src={selectedMedia.url.replace('w=800', 'w=1920')}
                  alt={selectedMedia.title}
                  className="w-full h-auto max-h-[90vh] object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 text-white">
                  <h2 className="text-2xl font-bold mb-2">{selectedMedia.title}</h2>
                  <p className="text-slate-300">{selectedMedia.description}</p>
                </div>
              </div>
            ) : (
              <div className="relative aspect-video">
                <iframe
                  src={selectedMedia.url}
                  title={selectedMedia.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Modal */}
      {editMode && editingItem && (
        <Dialog open={editMode} onOpenChange={() => setEditMode(false)}>
          <DialogContent className="max-w-2xl">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Edit Gallery Item</h2>
              
              <div>
                <Label>Type</Label>
                <p className="text-sm text-slate-600 mt-1">{editingItem.type}</p>
              </div>

              <ImageUpload
                label="Image/Video URL"
                value={editingItem.url}
                onChange={(url) => setEditingItem({...editingItem, url: url})}
              />

              <div>
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editingItem.description}
                  onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                  rows={3}
                />
              </div>

              {editingItem.type === 'video' && (
                <ImageUpload
                  label="Thumbnail URL (optional - auto-generated for YouTube)"
                  value={editingItem.thumbnail || ''}
                  onChange={(url) => setEditingItem({...editingItem, thumbnail: url})}
                />
              )}

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} className="bg-amber-600 hover:bg-amber-700">
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Item Modal */}
      {showAddForm && (
        <Dialog open={showAddForm} onOpenChange={() => setShowAddForm(false)}>
          <DialogContent className="max-w-2xl">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Add Gallery Item</h2>
              
              <div>
                <Label htmlFor="type">Type</Label>
                <Select value={newItem.type} onValueChange={(value) => setNewItem({...newItem, type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <ImageUpload
                label="Image/Video URL *"
                value={newItem.url}
                onChange={(url) => setNewItem({...newItem, url: url})}
              />

              {newItem.type === 'video' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <p className="font-semibold mb-1">YouTube Video Tips:</p>
                  <p>• You can paste any YouTube link format (watch?v= or youtu.be)</p>
                  <p>• It will be automatically converted to embed format</p>
                  <p>• Thumbnail will be auto-generated from YouTube if not provided</p>
                </div>
              )}

              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={newItem.title}
                  onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                  rows={3}
                />
              </div>

              {newItem.type === 'video' && (
                <ImageUpload
                  label="Thumbnail URL (optional - auto-generated for YouTube)"
                  value={newItem.thumbnail}
                  onChange={(url) => setNewItem({...newItem, thumbnail: url})}
                />
              )}

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddItem} className="bg-amber-600 hover:bg-amber-700">
                  Add Item
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Gallery;
